'use client';

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from '@/lib/auth-context';
import { TRPCProvider } from '@/lib/trpc';
import { ServiceWorkerRegistrar } from '@/components/ServiceWorkerRegistrar';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MaintenanceGate } from '@/components/MaintenanceGate';
import '@/lib/i18n'; // side-effect: initialise i18next once

/**
 * Reads the user's saved locale from localStorage on first mount and
 * applies it (i18n language + document dir/lang) so every route —
 * login, dashboard, public — starts in the persisted language.
 */
function LocaleRestorer() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = localStorage.getItem('slikerr_locale');
    if (saved === 'he' || saved === 'en') {
      if (saved !== i18n.language) {
        void i18n.changeLanguage(saved);
      }
      document.documentElement.dir = saved === 'he' ? 'rtl' : 'ltr';
      document.documentElement.lang = saved;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <TRPCProvider>
        <LocaleRestorer />
        {/* Provider-level on purpose: /dashboard/* is a different layout tree,
            and impersonation is exactly the case where the admin is over
            there. Renders null unless an impersonation is (or just was)
            active. */}
        <ImpersonationBanner />
        <ServiceWorkerRegistrar />
        {/* Tranche K. Renders the "back shortly" screen INSTEAD of the page
            while maintenance is on — but never over /admin or /login, or the
            screen that turns maintenance off would be hidden by maintenance
            being on. Fails open: loading and errored both render the app. */}
        <MaintenanceGate>{children}</MaintenanceGate>
      </TRPCProvider>
    </AuthProvider>
  );
}
